/**
 * @module Experience/repdash
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

// core libraries
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SETTINGS } from '@environment/settings';
// interfaces
import { Line } from '@experience/landing/common/components/account-landing/interfaces/account-lines-device-details.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { CustomerAccountLineDeviceDetails } from '@support/api/proxies/responses/api-2022.interface';
// Services
import { AccountLandingService } from '@experience/landing/common/components/account-landing/services/account-landing.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { UserService } from '@support/shared/services/user.service';
// constants
import { AccountLineDetailsModel } from '@experience/landing/common/components/account-landing/models/account-line-details.model';
import { ACCOUNT_LANDING_CONSTANTS } from '@experience/landing/common/constants/account-landing.constants';

@Component({
  selector: 'tmo-p10m-line-details',
  templateUrl: './p10m-line-details.component.html',
  styleUrls: [],
})
export class P10MLineDetailsComponent implements OnInit, OnDestroy {
  /**
   * stores line details as input from account landing
   */
  @Input() public accountLineDetailsModel: AccountLineDetailsModel;
  /**
   * stores primary msisdn
   */
  @Input() public primaryMsisdn: string;
  /**
   * holds content data received from account landing
   */
  @Input() public contentData: ConfigData;
  /**
   * stores url to show device image
   */
  public extTmoImagesUrl: string;
  /**
   * stores default image url
   */
  public imageUrl: string;
  /**
   * stores primary constant value
   */
  public primaryConstant: string;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;

  constructor(
    private accountLandingService: AccountLandingService,
    private commonUtilService: CommonUtilService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private userService: UserService
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.extTmoImagesUrl = SETTINGS.EXT_IMAGE_URL;
    this.primaryConstant = ACCOUNT_LANDING_CONSTANTS.PRIMARY.toLowerCase();
  }
  /**
   * Loads the initial data of Account Landing Page
   * @returns void
   */
  public ngOnInit(): void {
    const index: number = 0;
    this.getDeviceDetails(this.stateStoreUtilsService.getMsisdn(), index);
    this.imageUrl = this.commonUtilService.getImageUrl('');
  }
  /**
   * Method to set device details on every line blade click
   * @param {string} msisdn
   * @param {number} index
   * @returns void
   */
  public getDeviceDetails(msisdn: string, index: number): void {
    this.accountLandingService
      .getCustomerLineDeviceDetails(msisdn)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((customerAccountLineDeviceDetails: CustomerAccountLineDeviceDetails) => {
        this.accountLineDetailsModel.getDeviceDetails(customerAccountLineDeviceDetails, index);
      });
  }
  /**
   * function to set line sub status
   * @param {Line} lineDetails
   * @returns {string}
   * @memberof P10MLineDetailsComponent
   */
  public getSubStatus(lineDetails: Line): string {
    return this.userService.getLineStatus(lineDetails);
  }

  /**
   * Unsubscribes for all subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
